import type { MeterDetail } from '@rheora/enrollment-contract';

import { enrollmentTypeLabelKey } from './serviceability.service';

/**
 * Meter-detail presentation logic (SHOP-3 / SHOP-4) — kept OUT of the `.tsx`
 * per CLAUDE.md. Both the `MeterSummaryCard` (compact "your meter" card) and the
 * `MeterDetailsDialog` ("see all meter details") derive their rows from these
 * pure functions, so the two surfaces can never drift and the components stay
 * render-only.
 *
 * Every row is either a literal display `text` (address, TDU, ESI ID) or an i18n
 * `valueKey` (+ optional `valueParams` for interpolation) — never raw copy, and
 * the service never calls `t()`. The component resolves the keys.
 *
 * ── AC3 POSTURE ────────────────────────────────────────────────────────────
 * `esiid` is DISPLAY-ONLY — a confirmation of the customer's own resolved meter,
 * never a redemption key (that is the opaque `candidateId`, deliberately absent
 * from every row).
 */

/** One row of a meter card / dialog. */
export interface MeterField {
  /** Stable id for the React key + tests. */
  readonly id: string;
  /** i18n key for the field label. */
  readonly labelKey: string;
  /** A literal, already-display-ready value (address, TDU, ZIP, ESI ID). */
  readonly text?: string;
  /** An i18n key whose translation IS the value (customer type, load zone, …). */
  readonly valueKey?: string;
  /** Interpolation params for `valueKey` (e.g. the grouped usage number). */
  readonly valueParams?: Readonly<Record<string, string | number>>;
  /**
   * i18n key for a warning BADGE beside the value (E-SF19 P2).
   *
   * Present only when the value is not what the heading claims — today, an
   * example address rendered under "Meter details confirmed". Absent means the
   * value is the real thing; there is no "everything is fine" badge.
   */
  readonly badgeKey?: string;
}

/**
 * "123 Main St, Dallas, TX 75201" — the structured premise address as one line.
 *
 * ── THE CLIENT-SIDE EMPTY-STREET GUARD (E-SF19 P0b) ────────────────────────
 * A premise with no street used to render as ", DALLAS, TX 75201" — a leading
 * comma where the customer's own address should be, which reads as a broken page
 * rather than as missing data, and which a customer cannot recognise as theirs.
 *
 * GMO-E12 now filters those rows upstream and the BFF filters again
 * (`isOfferablePremise`); a live sweep of all 20 catalogue ZIPs on 2026-08-26
 * found zero. This is the third line: EMPTY PARTS ARE DROPPED, so the worst case
 * is a shorter address rather than a punctuation artefact. It is cheap, it is
 * local, and it is the only one of the three that cannot be regressed by a change
 * in another repository.
 */
export function formatMeterAddress(meter: MeterDetail): string {
  const { line1, unit, city, state, zip } = meter.address;
  const street = [line1, unit].map((part) => part?.trim() ?? "").filter((part) => part !== "");
  const parts = [street.join(" "), city.trim(), `${state} ${zip}`.trim()];
  return parts.filter((part) => part !== "").join(", ");
}

/** Grouped annual usage number (e.g. 11500 → "11,500"); the unit rides the i18n value. */
export function formatUsageKwh(kwh: number): string {
  return kwh.toLocaleString('en-US');
}

/** i18n key for the customer-type value (Residential / Business). */
export function customerTypeValueKey(meter: MeterDetail): string {
  return `shop.meter.field.customerType.${meter.customerType}`;
}

/** i18n key for the ERCOT load-zone value. */
export function loadZoneValueKey(meter: MeterDetail): string {
  return `shop.meter.field.loadZone.${meter.loadZone}`;
}

/** i18n key for the smart-meter value (a friendly yes/no + the meter class). */
export function meterTypeValueKey(meter: MeterDetail): string {
  return meter.isSmartMeter
    ? 'shop.meter.field.meterType.smart'
    : `shop.meter.field.meterType.${meter.meterType}`;
}

/** i18n key for the service-status value. */
export function serviceStatusValueKey(meter: MeterDetail): string {
  return `shop.meter.field.serviceStatus.${meter.serviceStatus}`;
}

/**
 * The compact card rows: ZIP · address · TDU · usage · customer type. The
 * enrollment-type badge is rendered separately by the card, so it is not here.
 */
export function meterSummaryFields(meter: MeterDetail): readonly MeterField[] {
  return [
    { id: 'zip', labelKey: 'shop.meter.summary.field.zip', text: meter.zip },
    {
      id: 'address',
      labelKey: 'shop.meter.summary.field.address',
      text: formatMeterAddress(meter),
      // ⚠️ BADGED WHEN IT IS AN EXAMPLE (E-SF19 P2). The dev resolver synthesises
      // a meter for any well-formed known-prefix ESI ID and substitutes a generic
      // street for one it has no premise for — and this card renders it under
      // "Meter details confirmed" with nothing to say it is a stand-in.
      // `STUBS.md` promised such data would be badged; it was not.
      ...(meter.addressIsPlaceholder === true ? { badgeKey: 'shop.meter.field.address.placeholder' } : {}),
    },
    { id: 'tdu', labelKey: 'shop.meter.summary.field.tdu', text: meter.tdsp },
    {
      id: 'usage',
      labelKey: 'shop.meter.summary.field.usage',
      valueKey: 'shop.meter.field.usage.value',
      valueParams: { kwh: formatUsageKwh(meter.estimatedAnnualUsageKwh) },
    },
    {
      id: 'customerType',
      labelKey: 'shop.meter.summary.field.customerType',
      valueKey: customerTypeValueKey(meter),
    },
  ];
}

/**
 * The full "see all meter details" field set (research §2): the resolved meter's
 * identity/territory, meter hardware, usage character, and enrollment routing.
 */
export function meterDetailFields(meter: MeterDetail): readonly MeterField[] {
  return [
    { id: 'esiid', labelKey: 'shop.meter.detail.field.esiid', text: meter.esiid },
    {
      id: 'premise',
      labelKey: 'shop.meter.detail.field.premise',
      text: formatMeterAddress(meter),
      ...(meter.addressIsPlaceholder === true ? { badgeKey: 'shop.meter.field.address.placeholder' } : {}),
    },
    { id: 'zip', labelKey: 'shop.meter.detail.field.zip', text: meter.zip },
    { id: 'tdu', labelKey: 'shop.meter.detail.field.tdu', text: meter.tdsp },
    {
      id: 'customerType',
      labelKey: 'shop.meter.detail.field.customerType',
      valueKey: customerTypeValueKey(meter),
    },
    {
      id: 'usage',
      labelKey: 'shop.meter.detail.field.usage',
      valueKey: 'shop.meter.field.usage.value',
      valueParams: { kwh: formatUsageKwh(meter.estimatedAnnualUsageKwh) },
    },
    { id: 'loadZone', labelKey: 'shop.meter.detail.field.loadZone', valueKey: loadZoneValueKey(meter) },
    {
      id: 'meterType',
      labelKey: 'shop.meter.detail.field.smartMeter',
      valueKey: meterTypeValueKey(meter),
    },
    {
      id: 'serviceStatus',
      labelKey: 'shop.meter.detail.field.serviceStatus',
      valueKey: serviceStatusValueKey(meter),
    },
    {
      id: 'enrollmentType',
      labelKey: 'shop.meter.detail.field.enrollmentType',
      valueKey: enrollmentTypeLabelKey(meter.enrollmentType),
    },
  ];
}
